import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const NHSScheduling: React.FC = () => {
  const navigate = useNavigate()
  
  // Reading progress
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  
  // Active section highlight
  const [activeId, setActiveId] = useState<string>('exec-summary')

  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: "exec-summary", labelKey: "NHSScheduling.sections.executiveSummary" },
    { id: "legacy-problem", labelKey: "NHSScheduling.sections.legacyProblem" },
    { id: "nhs-strategy", labelKey: "NHSScheduling.sections.nhsStrategy" },
    { id: "smart-automation", labelKey: "NHSScheduling.sections.smartAutomation" },
    { id: "benefits", labelKey: "NHSScheduling.sections.benefits" },
    { id: "implementation", labelKey: "NHSScheduling.sections.implementation" },
    { id: "case-modernisation", labelKey: "NHSScheduling.sections.caseModernisation" },
    { id: "references", labelKey: "NHSScheduling.sections.references" },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: "-40% 0px -50% 0px", threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Smooth scroll to anchors
  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // Get actual header height dynamically
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80 // 16px extra padding
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: "smooth" })
  }

  // Helper UI component
  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Reading progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | NHS Market Focus
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            From Legacy PAS to Intelligent Scheduling: Integrating AI and Smart Automation in NHS Pathway and Capacity Management
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Across the NHS, legacy Patient Administration Systems (PAS) continue to anchor scheduling, admissions, and outpatient workflows. While foundational, these systems were never designed to manage complex, multi-pathway scheduling across Integrated Care Systems (ICS).
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX | Collaborative Healthcare Platform" />
            <MetaItem label="Published" value="October 15, 2025" />
            <MetaItem label="Length" value="15 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: "From Legacy PAS to Intelligent Scheduling", url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      {/* Content & Sidebar */}
      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article */}
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? "mt-10" : ""}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === "exec-summary" && "⭐ Executive Summary"}
                {s.id === "legacy-problem" && "🔴 1. The Legacy Problem: PAS and Scheduling Silos"}
                {s.id === "nhs-strategy" && "🏥 2. The NHS Digital Strategy: The Push Toward Integration"}
                {s.id === "smart-automation" && "🤖 3. Smart Automation and Predictive Analytics in Scheduling"}
                {s.id === "benefits" && "📊 4. Benefits for Trusts and ICSs"}
                {s.id === "implementation" && "🛠️ 5. Implementation Roadmap for NHS Trusts"}
                {s.id === "case-modernisation" && "✅ 6. The Case for Modernisation: Strategic Impact"}
                {s.id === "references" && "📚 References"}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === "exec-summary" && (
                  <>
                    <p>
                      Across the NHS, legacy Patient Administration Systems (PAS) continue to anchor scheduling, admissions, and outpatient workflows. While foundational, these systems were never designed to manage complex, multi-pathway scheduling across Integrated Care Systems (ICS), nor to provide embedded functionality that integrates seamlessly into the clinical workflow and aids productivity at the bedside.
                    </p>
                    <br />
                    <p>
                      Increasingly, the modern NHS requires scheduling and referral tools that can operate at every point of care—from the patient's home to the clinic. The ability to initiate and manage scheduling processes remotely, in a way that mirrors the simplicity of <strong>Amazon-like ordering</strong>, is now critical to patient engagement and operational efficiency. These solutions also play a growing role in supporting the NHS's <strong>digital front door</strong> vision—offering patients, carers, and referrers a single, intuitive entry point to access and coordinate care.
                    </p>
                    <br />
                    <p>
                      These processes may start in primary care, feed directly into triaged waiting lists, and drive downstream scheduling based on multiple clinical and operational factors.
                    </p>
                    <br />
                    <p>
                      Some of the most complex scheduling challenges occur in oncology, particularly chemotherapy, where treatment protocols, patient fitness, and ongoing test results determine whether therapy continues or pauses. In such settings, scheduling must dynamically adjust as blood results or other investigations become available. Integrating these medical orders and result messages directly into the scheduling system can create departmental dashboards that flag patient readiness, streamline workflows, and save nurse time previously spent chasing results.
                    </p>
                    <br />
                    <p>
                      This approach has already been demonstrated in practice—for example, at Clatterbridge Cancer Hospital (Wirral), where <strong>EceptionistCX</strong> was deployed to support chemotherapy scheduling, protocol adherence, and patient flow across complex oncology pathways.
                    </p>
                    <br />
                    <p>
                      This white paper explores how NHS organisations can transition from static, PAS-centric scheduling to intelligent, integrated scheduling and ordering systems supported by smart automation and predictive analytics. By leveraging cloud-native tools, interoperability standards such as FHIR, and data-driven optimisation, Trusts can achieve more efficient resource allocation, reduced Did-Not-Attend (DNA) rates, and improved patient experience. The goal is not merely replacing old technology—it's enabling the modern digital NHS envisioned by What Good Looks Like and the Frontline Digitisation frameworks.
                    </p>
                  </>
                )}
                {s.id === "legacy-problem" && (
                  <>
                    <p>
                      Many NHS Trusts still rely on outdated PAS platforms—often decades old—for managing patient data, scheduling, and reporting. Systems such as iPM, Lorenzo, and older versions of Cerner Millennium were designed for basic administrative functions, not the dynamic scheduling needs of integrated care.
                      These legacy systems are often clunky, difficult to maintain, and require specialist teams to support daily operation and configuration. They do not integrate well with modern messaging or interoperability platforms and frequently depend on third-party tools to enable even basic data exchange. Implementation and upgrades are typically long and expensive, as they are tied to other PAS functionality that must be adapted in parallel.
                    </p>
                    <br />
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <img 
                        src="/Image/WhitePapers/NHS_Intelligent_Scheduling_WhitePaper_Final/image1.png" 
                        alt="Legacy PAS Challenges Diagram"
                        className="w-full rounded-lg shadow-sm"
                      />
                    </div>
                    <br />
                    <p>
                      This creates inefficiencies, duplicated effort, and delays in coordination. Outpatient clinics, diagnostics, theatres, and community services often operate as separate systems with limited visibility. The lack of real-time integration leads to scheduling conflicts, data silos, and significant administrative overhead.
                      As the Frontline Digitisation initiative notes, legacy PAS systems "impede interoperability, contribute to elective backlogs, and limit the ability to optimise clinical workflows."
                    </p>
                  </>
                )}
                {s.id === "nhs-strategy" && (
                  <>
                    <p>
                      NHS England's What Good Looks Like (WGLL) and Frontline Digitisation programmes provide clear guidance for digital transformation. These frameworks emphasise interoperability, patient-centred design, and data-driven operations.
                      The Elective Recovery Plan (2023) highlights digital scheduling and patient engagement as critical to reducing waiting lists. Together, these initiatives establish the foundation for modern, intelligent scheduling platforms that coordinate across the full care continuum—from referral to recovery.
                    </p>
                  </>
                )}
                {s.id === "smart-automation" && (
                  <>
                    <p>
                      Smart automation and predictive analytics enable Trusts to use existing data to make real-time decisions about appointments, capacity, and staffing. These technologies support clinicians rather than replace them, automating repetitive administrative tasks and surfacing actionable insights.
                    </p>
                    <br />
                    <p>
                      <strong>Examples include:</strong>
                    </p>
                    <ul>
                      <li>• Predictive modelling for outpatient and diagnostic scheduling.</li>
                      <li>• Automated prioritisation for urgent or complex cases.</li>
                      <li>• Data-driven insights to reduce DNAs and cancellations.</li>
                      <li>• Capacity visibility across multi-site systems and ICS partners.</li>
                      <li>• Smart dashboards to aid workflows in clinics, departments, and monitoring teams—with linked datasets (e.g., blood orders and results) embedded directly into schedules to support real-time decision-making.</li>
                    </ul>
                    <br />
                    <p>
                      The NHS AI Lab has funded pilots in these areas, demonstrating how intelligent automation helps balance workloads and improve operational efficiency.
                    </p>
                  </>
                )}
                {s.id === "benefits" && (
                  <>
                    <p>
                      Modernising scheduling delivers measurable improvements across operational and clinical metrics:
                    </p>
                    <br />
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <img 
                        src="/Image/WhitePapers/NHS_Intelligent_Scheduling_WhitePaper_Final/image2.png" 
                        alt="Benefits for Trusts and ICSs"
                        className="w-full rounded-lg shadow-sm"
                      />
                    </div>
                  </>
                )}
                {s.id === "implementation" && (
                  <>
                    <p>
                      Moving away from legacy PAS does not require a complete system replacement. Trusts can adopt a phased, standards-based approach:
                    </p>
                    <br />
                    <ol>
                      <li>1. Assess current scheduling and integration gaps.</li>
                      <li>2. Integrate interoperable modules via FHIR or HL7 APIs.</li>
                      <li>3. Automate capacity management and patient communication.</li>
                      <li>4. Engage staff and patients through co-design.</li>
                      <li>5. Measure outcomes using What Good Looks Like (WGLL) metrics — NHS England's framework for digital maturity, focusing on leadership, smart foundations, interoperability, and measurable improvements in care delivery.</li>
                    </ol>
                    <br />
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <img 
                        src="/Image/WhitePapers/NHS_Intelligent_Scheduling_WhitePaper_Final/image3.png" 
                        alt="Implementation Roadmap"
                        className="w-full rounded-lg shadow-sm"
                      />
                    </div>
                  </>
                )}
                {s.id === "case-modernisation" && (
                  <>
                    <p>
                      Modernising scheduling is a strategic enabler for the NHS's long-term success. Integrated, intelligent scheduling supports:
                    </p>
                    <br />
                    <ul>
                      <li>• Elective recovery and reduced waiting times.</li>
                      <li>• Workforce optimisation and reduced burnout.</li>
                      <li>• Patient empowerment through improved communication.</li>
                      <li>• ICS-wide coordination and visibility.</li>
                    </ul>
                    <br />
                    <p>
                      In addition to supporting existing digital transformation programmes, the <strong>EceptionistCX</strong> platform is well positioned to help organisations participate in the NHS's forthcoming <strong>Online Hospital initiative</strong>, announced by Prime Minister Keir Starmer. The initiative aims to deliver up to 8.5 million virtual appointments by 2027 through a digital platform integrated within the NHS App.
                    </p>
                    <br />
                    <p>
                    EceptionistCX’s comprehensive suite—including referrals, eConsults, telehealth, scheduling, and advice and guidance—can serve as a critical enabler for this initiative. The platform can operate both <strong>in the foreground and background alongside the NHS App</strong>, extending “front door” access to patients while powering secure, interoperable workflows behind the scenes. This capability allows Trusts and Integrated Care Systems to connect virtual consultations, assessments, and follow-up appointments with existing PAS and EPR systems—supporting hybrid care models, improving accessibility, and reducing waiting lists.
                    </p>
                    <br />
                    <p>
                        <strong>Real-World Example: Clatterbridge Cancer Hospital (Wirral) </strong>
                    </p>
                    <br />
                    <p className="text-sm text-slate-700">
                        At Clatterbridge Cancer Hospital, EceptionistCX was implemented to manage the complex scheduling requirements of oncology and chemotherapy services. The system dynamically built treatment and patient schedules based on chemotherapy protocols and blood-test results, automatically flagging when patients were ready for treatment. By integrating medical orders and laboratory results, the hospital created real-time departmental dashboards that improved visibility, reduced manual chasing, and freed nurse time for direct patient care.
                    </p>
                    <br />
                    <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-6">
                      <h4 className="text-lg font-semibold mb-4 text-blue-900">ℹ️ About Eceptionist</h4>
                      <p className="text-sm text-blue-900 mb-3">
                        Eceptionist has been delivering secure, cloud-based solutions for healthcare organisations globally since 1999, supporting leading hospitals, health systems, and government agencies across North America, the UK, Europe, and Australia.
                      </p>
                      <p className="text-sm text-blue-900 mb-3">
                        In the United Kingdom, Eceptionist has worked with NHS Trusts and other healthcare organisations since 2001, providing interoperable scheduling, referral management, and workflow tools that align with NHS England's What Good Looks Like and Frontline Digitisation priorities.
                      </p>
                      <p className="text-sm text-blue-900 mb-3">
                        The EceptionistCX platform is fully modular and designed to integrate with leading Electronic Patient Record (EPR) and Patient Administration System (PAS) solutions used across the NHS, including Oracle Cerner, Epic, Allscripts, Lorenzo, and others. Using standards-based approaches such as FHIR, HL7, and API-based messaging, EceptionistCX enables seamless data exchange while maintaining information governance and security compliance.
                      </p>
                      <p className="text-sm text-blue-900 mb-2">
                        <strong>Our UK experience includes:</strong>
                      </p>
                      <ul className="text-sm text-blue-900 space-y-2 pl-5">
                        <li>• Successful integration with multiple PAS and EPR platforms to support scheduling, referrals, eConsults, advice and guidance, telehealth, and other collaborative workflows.</li>
                        <li>• Deployment of solutions that align with NHS Digital and NHS England interoperability standards.</li>
                        <li>• Collaboration with Trusts focused on elective recovery, digital transformation, and improved patient access.</li>
                      </ul>
                      <p className="text-sm text-blue-900 mt-3">
                        Eceptionist's mission is to help healthcare organisations move from fragmented, legacy systems to connected, data-driven operations — improving care delivery, efficiency, and patient experience.
                      </p>
                    </div>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}
                {s.id === "references" && (
                  <>
                    <ol>
                      <li>1. NHS England. What Good Looks Like: A Framework for NHS Digital Maturity. 2021. <a href="https://www.england.nhs.uk/publication/what-good-looks-like-framework/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.england.nhs.uk/publication/what-good-looks-like-framework/</a></li>
                      <li>2. NHS England. Frontline Digitisation Programme. 2023. <a href="https://transform.england.nhs.uk/key-tools-and-info/frontline-digitisation/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://transform.england.nhs.uk/key-tools-and-info/frontline-digitisation/</a></li>
                      <li>3. NHS England. Elective Recovery Plan. 2023. <a href="https://www.england.nhs.uk/coronavirus/elective-care-recovery/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.england.nhs.uk/coronavirus/elective-care-recovery/</a></li>
                      <li>4. NHS AI Lab. AI in the NHS: Case Studies and Insights. 2023. <a href="https://transform.england.nhs.uk/ai-lab/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://transform.england.nhs.uk/ai-lab/</a></li>
                      <li>5. The Health Foundation. Digital Transformation and the Future of NHS Operations. 2022. <a href="https://www.health.org.uk/publications/reports/digital-transformation-and-the-future-of-nhs-operations" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.health.org.uk/publications/reports/digital-transformation-and-the-future-of-nhs-operations</a></li>
                      <li>6. HIMSS Europe. Digital Maturity in the NHS. 2022. <a href="https://www.himss.eu/resources/digital-maturity-nhs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.himss.eu/resources/digital-maturity-nhs</a></li>
                    </ol>
                  </>
                )}
              </div>
            </section>
          ))}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                This white paper explores how NHS organisations can transition from static, PAS-centric scheduling to intelligent, integrated scheduling systems supported by smart automation and predictive analytics.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>Legacy PAS limitations and challenges</li>
                <li>NHS digital strategy alignment</li>
                <li>Smart automation and AI capabilities</li>
                <li>Real-world implementation at Clatterbridge</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            {/* TOC */}
            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        "text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 " +
                        (activeId === s.id ? "font-semibold text-slate-900" : "text-slate-700")
                      }
                    >
                      {s.id === "exec-summary" && "Executive Summary"}
                      {s.id === "legacy-problem" && "The Legacy Problem"}
                      {s.id === "nhs-strategy" && "NHS Digital Strategy"}
                      {s.id === "smart-automation" && "Smart Automation"}
                      {s.id === "benefits" && "Benefits for Trusts and ICSs"}
                      {s.id === "implementation" && "Implementation Roadmap"}
                      {s.id === "case-modernisation" && "The Case for Modernisation"}
                      {s.id === "references" && "References"}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Citation */}
            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX | Collaborative Healthcare Platform. "From Legacy PAS to Intelligent Scheduling: Integrating AI and Smart Automation in NHS Pathway and Capacity Management." October 15, 2025.
              </p>
              <button
                onClick={() => {
                  const text = 'EceptionistCX | Collaborative Healthcare Platform. "From Legacy PAS to Intelligent Scheduling: Integrating AI and Smart Automation in NHS Pathway and Capacity Management." October 15, 2025.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Related white papers */}
      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "Digital Front Doors in Healthcare: Why Scheduling Matters More Than Ever", description: "Healthcare scheduling has emerged as one of the most strategic enablers of patient satisfaction, access to care, and provider efficiency.", route: "/whitepapers/scheduling-cx" },
              { title: "AI in Healthcare Operations", description: "Practical applications for smarter collaboration and better care delivery.", route: "/whitepapers/ai-in-healthcare-operations" },
              { title: "Breaking Silos with Integrated Care Coordination", description: "How integrated platforms improve outcomes and reduce costs across healthcare systems.", route: "/whitepapers/integrated-care" }
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="From Legacy PAS to Intelligent Scheduling: Integrating AI and Smart Automation in NHS Pathway and Capacity Management"
        pdfPath="/PDF/WhitePapers/NHS_Intelligent_Scheduling_WhitePaper_Final.pdf"
      />
    </div>
  )
}

export default NHSScheduling

